import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  TablePagination,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { createTiposVulnerabilidades, getTiposVulnerabilidad, updateTiposVulnerabilidades, deleteTiposVulnerabilidades } from '../../services/vulnerabilidades/tiposVulnerabilidad/tiposVulnerabilidadData';
import { toast } from "react-toastify";

const TablaGTV = () => {
  const [datos, setDatos] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [modificarIndex, setModificarIndex] = useState(-1);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tiposVulnerabilidadData = await getTiposVulnerabilidad();
        setDatos(Array.isArray(tiposVulnerabilidadData) ? tiposVulnerabilidadData : []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setDatos([]);
      }
    };
    fetchData();
  }, []);

  const [formulario, setFormulario] = useState({
    Descripcion: '',
  });

  const handleBusquedaChange = (event) => {
    setBusqueda(event.target.value);
    setPage(0);
  };

  const handleInsert = () => {
    setModificarIndex(-1);
    setFormulario({
      Descripcion: '',
    });
    setModalOpen(true);
  };

  const handleFormChange = (event) => {
    setFormulario({
      ...formulario,
      [event.target.name]: event.target.value,
    });
  };

  const handleGuardar = async () => {
    if (!formulario.Descripcion || formulario.Descripcion.trim() === "") {
      toast.warning("La descripción es obligatoria");
      return;
    }
    const dataToSend = {
      tvu_descripcion: formulario.Descripcion,
    };
    try {
      if (modificarIndex !== -1) {
        await updateTiposVulnerabilidades(modificarIndex, dataToSend);
        const tiposVulnerabilidadData = await getTiposVulnerabilidad();
        setDatos(Array.isArray(tiposVulnerabilidadData) ? tiposVulnerabilidadData : []);
        toast.success("Tipo de vulnerabilidad modificado correctamente");
      } else {
        await createTiposVulnerabilidades(dataToSend);
        const tiposVulnerabilidadData = await getTiposVulnerabilidad();
        setDatos(Array.isArray(tiposVulnerabilidadData) ? tiposVulnerabilidadData : []);
        toast.success("Tipo de vulnerabilidad creado correctamente");
      }
      setModalOpen(false);
      setFormulario({
        Descripcion: '',
      });
    } catch (error) {
      console.error("Error saving vulnerability type:", error);
      toast.error("Error al guardar el tipo de vulnerabilidad");
    }
  };

  const handleCancelar = () => {
    setModalOpen(false);
    setFormulario({
      Descripcion: '',
    });
  };

  const handleModificar = (index) => {
    setModificarIndex(datos[index].tvu_codigo);
    setFormulario({
      Descripcion: datos[index].tvu_descripcion,
    });
    setModalOpen(true);
  };

  const handleEliminar = (index) => {
    setDeleteIndex(index);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    const codigoToDelete = Array.isArray(datos) ? datos[deleteIndex]?.tvu_codigo : undefined;
    if (!codigoToDelete) {
      toast.error("No se encontró el tipo de vulnerabilidad a eliminar.");
      setConfirmOpen(false);
      setDeleteIndex(null);
      return;
    }
    try {
      await deleteTiposVulnerabilidades(codigoToDelete);
      toast.success("Tipo de vulnerabilidad eliminado correctamente");
      const tiposVulnerabilidadData = await getTiposVulnerabilidad();
      setDatos(Array.isArray(tiposVulnerabilidadData) ? tiposVulnerabilidadData : []);
      setConfirmOpen(false);
      setDeleteIndex(null);
    } catch (error) {
      setConfirmOpen(false);
      const errorMsg = error?.message || error?.response?.data?.message || "No se puede eliminar el tipo porque está asociado en el sistema.";
      if (
        error?.status === 400 ||
        errorMsg.toLowerCase().includes("no se puede eliminar") ||
        errorMsg.toLowerCase().includes("asociado") ||
        error?.response?.data?.errno === 1451
      ) {
        setErrorMessage(errorMsg);
        setErrorModalOpen(true);
      } else {
        toast.error(errorMsg);
      }
      setDeleteIndex(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setDeleteIndex(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const datosFiltrados = Array.isArray(datos)
    ? datos.filter(
      (dato) =>
        dato.tvu_descripcion &&
        dato.tvu_descripcion.toLowerCase().includes(busqueda.toLowerCase())
    )
    : [];

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Buscar tipo de vulnerabilidad..."
          value={busqueda}
          onChange={handleBusquedaChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 300 } }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleInsert}
          sx={{
            backgroundColor: '#001F3F',
            color: '#fff',
            '&:hover': { backgroundColor: '#003366' },
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Nuevo Tipo
        </Button>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }}>Descripción</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#001F3F' }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {datosFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" sx={{ color: '#666' }}>
                    {datos.length === 0 ? "No hay tipos de vulnerabilidades registrados." : "No se encontraron resultados."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              datosFiltrados
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((dato, index) => (
                  <TableRow key={dato.tvu_codigo} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#001F3F' }}>
                        {dato.tvu_descripcion}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <IconButton onClick={() => handleModificar(index + (page * rowsPerPage))} size="small" sx={{ color: "#666" }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => handleEliminar(index + (page * rowsPerPage))} size="small" sx={{ color: "#d32f2f" }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={datosFiltrados.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
      />

      {/* Modal Formulario */}
      <Dialog
        open={modalOpen}
        onClose={handleCancelar}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 2, fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          {modificarIndex !== -1 ? 'Modificar Tipo de Vulnerabilidad' : 'Nuevo Tipo de Vulnerabilidad'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Descripción"
              name="Descripcion"
              fullWidth
              value={formulario.Descripcion}
              onChange={handleFormChange}
              variant="outlined"
              error={!formulario.Descripcion && modalOpen}
              helperText={!formulario.Descripcion && modalOpen ? "Campo obligatorio" : ""}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelar} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleGuardar}
            variant="contained"
            sx={{
              backgroundColor: '#001F3F',
              color: '#fff',
              '&:hover': { backgroundColor: '#003366' }
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmación Eliminación */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#666' }}>
            ¿Está seguro que desea eliminar este tipo de vulnerabilidad?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCancelDelete} sx={{ color: '#666' }}>Cancelar</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              '&:hover': { backgroundColor: '#c62828' }
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de error */}
      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#001F3F', fontSize: '1.25rem' }}>
          No se puede eliminar
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
              {errorMessage || 'Este tipo de vulnerabilidad está siendo utilizado y no puede eliminarse.'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Antes de eliminarlo, asegúrate de actualizar o eliminar las relaciones donde se utiliza.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setErrorModalOpen(false)}
            variant="contained"
            sx={{ backgroundColor: '#001F3F', color: '#fff', '&:hover': { backgroundColor: '#003366' } }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TablaGTV;
